import type { MSite } from "@/types/core";
import { format } from "date-fns";
import { createPinia, setActivePinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";

import { MUser } from "@/types/account";

import { selectedSiteKey, sitesKey, userKey } from "./keys";
import "./main.css";

export function getSitesInfo(): { sites: MSite[]; selectedSite: MSite | null } {
  const sites = JSON.parse(
    document.getElementById("shops-data")?.textContent || "[]",
  );
  const selectedSiteStorageKey = "selectedShop";

  let selectedSiteId = localStorage.getItem(selectedSiteStorageKey);
  let selectedSite = null;

  if (selectedSiteId && sites && sites?.length) {
    selectedSite = sites.find((s: any) => s.uid === selectedSiteId);
    // shop might have deleted for some reason - during development
    if (!selectedSite) {
      selectedSite = sites[0];
      localStorage.setItem(selectedSiteStorageKey, selectedSite.uid);
    }
  } else {
    if (sites.length) {
      selectedSite = sites[0];
      localStorage.setItem(selectedSiteStorageKey, selectedSite.uid);
    }
  }

  return {
    sites,
    selectedSite,
  };
}

function getUser(): MUser | null {
  const userData = document.getElementById("logged-in-user")?.textContent;
  if (!userData) {
    return null;
  }
  try {
    return JSON.parse(userData);
  } catch (e) {
    console.error("Failed to parse user data:", e);
    return null;
  }
}

function getRootProps() {
  // get all json scripts whose id is like "vue-app-data:*"
  const rootProps: { [key: string]: any } = {};
  const jsonScripts = document.querySelectorAll(
    "script[id^='vue-app-data:'][type='application/json']",
  );
  jsonScripts.forEach((script) => {
    // the second part of the id coverted to camelCase is the prop name
    const id = script.id.replace("vue-app-data:", "");
    // covert id to camelCase
    const key = id.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const data = JSON.parse(script.textContent || "{}");

    // if the key is empty and the data is an object, spread the object
    // into the root props
    if (!key && typeof data === "object") {
      Object.assign(rootProps, data);
    } else {
      rootProps[key] = data;
    }
  });

  return rootProps;
}

export function injectApp(component: any, mount: boolean = true) {
  const appContainer = document.getElementById("__vue_app");
  if (!appContainer) {
    console.error(
      "No app container found. Please add a div with id '__vue_app' to your template.",
    );
    return;
  }

  const rootProps = getRootProps();

  const app = createApp(component, rootProps);
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);

  // provide selected shop to all components
  const { selectedSite, sites } = getSitesInfo();
  app.provide(selectedSiteKey, selectedSite);
  app.provide(sitesKey, sites);

  const user = getUser();
  app.provide(userKey, user);

  // register global components

  app.config.globalProperties.formatTimestamp = (
    timestamp: string,
    placeholder: string = "-",
  ) => {
    if (!timestamp) {
      return placeholder;
    }
    return format(new Date(timestamp), "MMM dd yyyy, hh:mm a");
  };
  app.config.globalProperties.formatDate = (date: string) => {
    return format(new Date(date), "MMM dd yyyy");
  };

  app.use(pinia);
  // This is required to use pinia in libraries like sitekit. Tried everything
  // but this is the only way to make it work. The library will have to use
  // window.__pinia to when using store.
  setActivePinia(pinia);
  (window as any).__pinia = pinia;

  // assume that the HTML templates will have this ID for the desired
  // mount point
  if (mount) {
    app.mount("#__vue_app");
  }
  return app;
}
